// src/Pages/CheckoutPage.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../contexts/CartContext";
import { useContentInfo } from "../contexts/ContentContext";
import { useShipping } from "../contexts/ShippingContext";
import { sendOrderEmails } from "../utils/sendOrderEmails";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { db } from "../firebase/firebase";
import toast from "../utils/toast";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const provinces = [
  "Punjab",
];

const citiesByProvince = {
  "Punjab": [
    "Gujrat",
  ]
};

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart: cartItems, clearCart } = useCart();
  const { contentInfo } = useContentInfo();
  const bankInfo = contentInfo?.bankInfo || {};
  const adminEmail = contentInfo?.contact?.email || "";

  // Buy Now mode: product passed directly via router state, bypassing the cart
  const buyNowItem = location.state?.buyNowItem || null;
  const isBuyNow = !!buyNowItem;
  const checkoutItems = isBuyNow ? [buyNowItem] : cartItems;

  useEffect(() => {
    document.title = "Checkout | dawaacare";
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    street: "", address2: "", city: "", region: "", postalCode: "",
    country: "Pakistan", orderNotes: "", paymentMethod: "cod"
  });

  const { shippingCost } = useShipping();
  const [showCartItems, setShowCartItems] = useState(true);
  const [placing, setPlacing] = useState(false);

  // Calculate totals using whichever items are active for this checkout
  const itemsTotal = checkoutItems.length > 0
    ? checkoutItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0)
    : 0;

  const currentShippingRate = checkoutItems.length > 0 ? shippingCost : 0;
  const grandTotal = itemsTotal + currentShippingRate;

  // Validation logic
  const isFormValid =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.phone.trim() !== "" &&
    formData.street.trim() !== "" &&
    formData.region.trim() !== "" &&
    formData.city.trim() !== "" &&
    formData.postalCode.trim() !== "";

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Complete order
  const orderViaWhatsApp = () => {
    if (checkoutItems.length === 0) {
      toast.error("No items to order!");
      return;
    }
    if (!isFormValid) {
      toast.error("Please fill in all required fields first.");
      return;
    }

    const rawNumber = (contentInfo?.contact?.whatsappNumber || "").replace(/\D/g, "");
    const whatsappNumber = rawNumber.startsWith("0") ? "92" + rawNumber.slice(1) : rawNumber;

    const itemLines = checkoutItems
      .map((item, i) => `  ${i + 1}. ${item.name} x${item.quantity} — Rs. ${item.price * item.quantity}`)
      .join("\n");

    const message = [
      "🛒 *New Order Request*",
      "",
      "*Customer Details:*",
      `  Name: ${formData.firstName} ${formData.lastName}`,
      `  Phone: ${formData.phone}`,
      `  Email: ${formData.email}`,
      "",
      "*Delivery Address:*",
      `  ${formData.street}${formData.address2 ? ", " + formData.address2 : ""}`,
      `  ${formData.city}, ${formData.region} ${formData.postalCode}`,
      `  ${formData.country}`,
      "",
      "*Order Items:*",
      itemLines,
      "",
      "*Order Summary:*",
      `  Subtotal: Rs. ${itemsTotal}`,
      `  Shipping: Rs. ${currentShippingRate}`,
      `  *Total: Rs. ${grandTotal}*`,
      "",
      `*Payment Method:* ${formData.paymentMethod === "cod" ? "Cash on Delivery" : "Bank Deposit"}`,
      formData.orderNotes ? `\n*Order Notes:* ${formData.orderNotes}` : "",
    ]
      .filter((line) => line !== undefined)
      .join("\n");

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encoded}`, "_blank");

    if (!isBuyNow) clearCart();

    navigate("/order-success", {
      state: {
        orderId: null,
        items: checkoutItems,
        customerDetails: formData,
        subtotal: itemsTotal,
        shipping: currentShippingRate,
        total: grandTotal,
      },
    });
  };

  const completeOrder = async (e) => {
    e.preventDefault();

    if (checkoutItems.length === 0) {
      toast.error("No items to order!");
      return;
    }

    setPlacing(true);
    try {
      const orderData = {
        customerDetails: formData,
        items: checkoutItems,
        subtotal: itemsTotal,
        shipping: shippingCost,
        total: grandTotal,
        status: "Pending",
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "orders"), orderData);

      const resolvedAdminEmail = adminEmail || "info@dawaacare.com";
      await sendOrderEmails(orderData, resolvedAdminEmail, { ...contentInfo?.contact, footerLogoURL: contentInfo?.footerLogoURL });

      if (!isBuyNow) clearCart();

      navigate("/order-success", {
        state: {
          orderId: docRef.id,
          items: checkoutItems,
          customerDetails: formData,
          subtotal: itemsTotal,
          shipping: currentShippingRate,
          total: grandTotal,
        },
      });

    } catch (error) {
      console.error(error);
      toast.error("Failed to place order ❌");
    } finally {
      setPlacing(false);
    }
  };
  return (
    <>
      <Header />

      <form className="checkout-wrapper" onSubmit={completeOrder}>

        {/* LEFT SIDE */}
        <div className="checkout-left">
          <h2>Customer Details</h2>
          <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} required />
          <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} required />
          <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required />
          <input type="text" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} required />

          <h2>Delivery Address</h2>
          <input type="text" name="street" placeholder="Street Address" value={formData.street} onChange={handleInputChange} required />
          <input type="text" name="address2" placeholder="Apartment, suite, etc [Optional]" value={formData.address2} onChange={handleInputChange} />
          <select name="region" value={formData.region} onChange={handleInputChange} required>
            <option value="">Select Province</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select name="city" value={formData.city} onChange={handleInputChange} required>
            <option value="">Select City</option>
            {formData.region && citiesByProvince[formData.region]?.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" name="postalCode" placeholder="Postal Code" value={formData.postalCode} onChange={handleInputChange} required />
          <textarea name="orderNotes" placeholder="Order Notes (Optional)" value={formData.orderNotes} onChange={handleInputChange} />

          <h2>Payment Method</h2>
          <div className="payment-method">
            <label>
              <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleInputChange} />
              Cash on Delivery {shippingCost > 0 ? `(Rs. ${shippingCost})` : "(Free)"}
            </label>
            <label>
              <input type="radio" name="paymentMethod" value="bank" checked={formData.paymentMethod === 'bank'} onChange={handleInputChange} />
              Bank Deposit
            </label>
          </div>

          {formData.paymentMethod === 'bank' && (
            <div className="bank-deposit-info">
              <p><strong>Bank:</strong> {bankInfo.bankName || "—"}</p>
              <p><strong>Account Holder:</strong> {bankInfo.accountHolder || "—"}</p>
              <p><strong>Account Number:</strong> {bankInfo.accountNumber || "—"}</p>
              <p>
                Note: Please Transfer Rs. {grandTotal} To The Above Account And Send The Screenshot On Whatsapp {bankInfo.whatsappNumber || "—"}.
              </p>
            </div>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="checkout-right">
          {/* Order Items Dropdown */}
          <div className="cart-items-header" onClick={() => setShowCartItems(prev => !prev)}>
            <h2 className=".cart-list">
              <span>{isBuyNow ? "Order Item" : "Cart Items"}&nbsp;&nbsp;</span>
              <FontAwesomeIcon icon={showCartItems ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} />
            </h2>
          </div>

          {showCartItems && (
            <div className="cart-items-list">
              {checkoutItems.length === 0 && <p>No items</p>}
              {checkoutItems.map(item => (
                <div key={item.id} className="order-item">
                  <img src={item.picture} alt={item.name} />
                  <div>
                    <p>{item.name}</p>
                    <p>Qty: {item.quantity}</p>
                    <p>Rs. {item.price}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2>Order Summary</h2>
          <p>Subtotal: Rs. {itemsTotal}</p>
          <p>Shipping: Rs. {currentShippingRate}</p>
          <h3>Total: Rs. {grandTotal}</h3>

          <button type="submit" className="complete-order-btn" disabled={checkoutItems.length === 0 || !isFormValid || placing}>
            {placing ? (
              <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>Placing Order...</>
            ) : "Complete Order"}
          </button>

          <div className="whatsapp-order-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="whatsapp-order-btn"
            onClick={orderViaWhatsApp}
            disabled={checkoutItems.length === 0 || !isFormValid}
          >
            <i className="fa-brands fa-whatsapp" style={{ marginRight: "8px", fontSize: "18px" }}></i>
            Order via WhatsApp
          </button>
        </div>

      </form>

      <Footer />
    </>
  );
};

export default CheckoutPage;